import { POSE_FLOATS, type CitizenAsset, type CitizenClip, type CitizenLod } from './citizenAsset';

/**
 * How a citizen is posed. A pose is a place in the pose table: a row and the share of the way to
 * the next one, as one number. A figure is drawn in one pose, or part of the way from one to
 * another, which is how it settles from a walk into standing. The city's shader makes the same
 * sums on every vertex (render/shaders/citizenSkin.glsl); `skinnedVertex` is those sums here, for
 * what must know where a figure's hand or foot is, and for the tests.
 */

/** A place in the pose table that draws nothing moved: the figure as it was modelled. */
export const UNPOSED = -1;

/** Where a clip stands `phase` of the way through its cycle; any phase, it wraps. */
export function clipPose(clip: CitizenClip, phase: number): number {
  return clip.row + (phase - Math.floor(phase)) * clip.rows;
}

/** A clip's place after `seconds` of playing it. */
export function clipPoseAt(clip: CitizenClip, seconds: number): number {
  return clipPose(clip, seconds / clip.seconds);
}

/** A walk's place for a figure `scale` times life size that has walked `metres`: its feet stay
 *  where they are set down. */
export function walkPose(clip: CitizenClip, metres: number, scale: number): number {
  if (!clip.strideM) throw new Error('citizens: a clip without a stride is not a walk');
  return clipPose(clip, metres / (clip.strideM * scale));
}

type Vec3 = [number, number, number];
type Quat = [number, number, number, number];

function turned(q: Quat, v: Vec3): Vec3 {
  // v + 2 q.xyz × (q.xyz × v + q.w v)
  const [x, y, z, w] = q;
  const cx = y * v[2] - z * v[1] + w * v[0],
    cy = z * v[0] - x * v[2] + w * v[1],
    cz = x * v[1] - y * v[0] + w * v[2];
  return [v[0] + 2 * (y * cz - z * cy), v[1] + 2 * (z * cx - x * cz), v[2] + 2 * (x * cy - y * cx)];
}

/* One bone at a place in the table: its turn, not yet of length one, and its shift. */
function boneAt(asset: CitizenAsset, pose: number, joint: number): { turn: Quat; shift: Vec3 } {
  const row = Math.floor(pose),
    f = pose - row;
  const a = (row * asset.bones.length + joint) * POSE_FLOATS,
    b = a + asset.bones.length * POSE_FLOATS;
  const p = asset.poses;
  const between = (i: number): number => p[a + i]! + (p[b + i]! - p[a + i]!) * f;
  return {
    turn: [between(0), between(1), between(2), between(3)],
    shift: [between(4), between(5), between(6)],
  };
}

/*
 * Attachments share the shader's blended bone transform so a held object cannot
 * drift away from its hand while the citizen changes pose.
 */
export function skinnedPoint(
  asset: CitizenAsset,
  joint: number,
  place: readonly [number, number, number],
  from: number,
  to: number = from,
  mix = 0
): Vec3 {
  if (from < 0) return [...place];
  const bone = boneAt(asset, from, joint);
  if (mix > 0) {
    const other = boneAt(asset, to, joint);
    const dot = bone.turn.reduce((sum, value, i) => sum + value * other.turn[i]!, 0);
    const sign = dot < 0 ? -1 : 1;
    bone.turn = bone.turn.map((value, i) => value + (other.turn[i]! * sign - value) * mix) as Quat;
    bone.shift = bone.shift.map((value, i) => value + (other.shift[i]! - value) * mix) as Vec3;
  }
  const length = Math.hypot(...bone.turn);
  const at = turned(bone.turn.map((value) => value / length) as Quat, [...place]);
  return at.map((value, i) => value + bone.shift[i]!) as Vec3;
}

/** Where vertex `v` of a figure lies, in metres in the figure's own frame, `mix` of the way from
 *  the pose `from` to the pose `to`. */
export function skinnedVertex(
  asset: CitizenAsset,
  lod: CitizenLod,
  v: number,
  from: number,
  to: number = from,
  mix = 0
): Vec3 {
  const place: Vec3 = [
    lod.places[v * 3]! * asset.positionStepM,
    lod.places[v * 3 + 1]! * asset.positionStepM,
    lod.places[v * 3 + 2]! * asset.positionStepM,
  ];
  if (from < 0) return place;
  const out: Vec3 = [0, 0, 0];
  for (let c = 0; c < 4; c++) {
    const weight = lod.weights[v * 4 + c]! / 255;
    if (weight === 0) continue;
    const joint = lod.joints[v * 4 + c]!;
    const at = skinnedPoint(asset, joint, place, from, to, mix);
    for (let i = 0; i < 3; i++) out[i]! += at[i]! * weight;
  }
  return out;
}
